import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { headset } from 'ionicons/icons';
import { Observable, of, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CacheService } from './cache.service';

export enum LOADING_STATUS {
  NOT_STARTED = 0,
  LOADING = 1,
  LOADED = 2,
  ERROR = 3,
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl: string = environment.baseUrl;
  private readonly TTL_MINUTES = 1440;

  cacheService = inject(CacheService);

  constructor(private http: HttpClient) {}

  // get filter options data
  setAdminSettingsBottlerRole(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/settings/admin/bottler-role-selection`, body);
  }

  getAdminSettingsBottlerRoleInfo(): Observable<any> {
    return this.http.get(`${this.baseUrl}/settings/admin/bottler-role-options`);
  }

  // getFilterValues(usecache: boolean = true): Observable<any> {
  //   const url = `/filter/get-filter-value/pref-filter`;
  //   // if (usecache) {
  //   //   const cached = this.cacheService.get(url);
  //   //   if (cached) {
  //   //     return of(cached);
  //   //   }
  //   // }

  //   return this.http.get(this.baseUrl + url);
  //   // .pipe(
  //   // tap((data) => {
  //   // this.cacheService.set(url, data, this.TTL_MINUTES);
  //   // }),
  //   // );
  // }

  getFilterValues(usecache: boolean = false): Observable<any> {
    const url = `/session/get-filter-value/pref-filter`;
    if (usecache) {
      const cached = this.cacheService.get(url);
      if (cached) {
        return of(cached);
      }
    }

    return this.http.get(this.baseUrl + url).pipe(
      tap((data) => {
        this.cacheService.set(url, data, this.TTL_MINUTES);
      }),
    );
  }

  getVoidFiltersValues(usecache: boolean = false): Observable<any> {
    const url = `/voids/void-filter`;
    if (usecache) {
      const cached = this.cacheService.get(url);
      if (cached) {
        return of(cached);
      }
    }

    return this.http.get(this.baseUrl + url).pipe(
      tap((data) => {
        this.cacheService.set(url, data, this.TTL_MINUTES);
      }),
    );
  }

  getUserPreference(usecache: boolean = false): Observable<any> {
    const url = `/users/get-user-preference`;
    if (usecache) {
      const cached = this.cacheService.get(url);
      if (cached) {
        return of(cached);
      }
    }
    return this.http.get(this.baseUrl + url).pipe(
      tap((data) => {
        this.cacheService.set(url, data, this.TTL_MINUTES);
      }),
    );
    // return this.http.get(`${this.baseUrl}/users/get-user-preference`);
  }

  applyFilter(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/apply-filter`, body);
  }

  getSummary(sessionId: string = '', body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/get-summary-data/${sessionId}`, body);
  }

  getFilterOnSummary(sessionId: string = '', body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/limit-detail/${sessionId}`, body);
  }

  removeRecommendations(filterGroups: any, sessionName: string, sessionId: string = ''): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/get-removed-data/${sessionId}`, {
      sessionName,
      filterGroups,
      userId: '1',
    });
  }

  downloadSAPZipData(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/download-sap/${sessionId}`, body);
  }

  downloadSAPZipHistoryFile(historyId: string, poType?: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/download-sap/${historyId}`, { purchaseOrderType: poType });
  }

  downloadFocusItemZipData(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/download-focus/${sessionId}`, body);
  }

  downloadFocusItemHistoryFile(historyId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/download-focus/${historyId}`, body);
  }

  downloadRecData(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/download/${sessionId}`, body);
  }
  
  requestGenAiEgress(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/egress-request/${sessionId}`, body);
  }

  getGenAiEgressList(sessionId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/session/fetch-egress-request-list/${sessionId}`);
  }

  downloadGenAiEgressZip(sessionId: string, body:any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/download-egress/${sessionId}`, body);
  }

  checkExistingEgressRequest(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/check-egress-request/${sessionId}`, body);
  }
  
  downloadExisitngGenAiEgressZip(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/download-egress/${sessionId}`, body);
  }

  requestHistoryGenAIEgress(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/egress-request/${sessionId}`, body);
  }

  checkPreviousMyCokeRequest(sessionId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/session/check-mycoke-request/${sessionId}`);
  }

  requestSendToMyCoke(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/send-to-mycoke/${sessionId}`, body);
  }

  requestHistorySendToMyCoke(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/history/send-to-mycoke/${sessionId}`, body);
  }

  fetchProcessStatus(sessionId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/history/fetch-process-status/${sessionId}`);
  }

  getGenAiAllowedBottlersList(): Observable<any> {
    return this.http.get(`${this.baseUrl}/session/get-genai-allowed-bottlers`);
  }

  downloadHistoryRecData(sessionId: string, downloadType: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/history/download/${sessionId}/${downloadType}`);
  }

  downloadMyCokeFormat(sessionId: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/download-mycoke/${sessionId}`, body);
  }

  downloadHistoryMyCokeFormat(sessionId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/history/download-mycoke/${sessionId}`);
  }

  downloadVoidZipData(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/voids/download-osr`, body);
  }

  downloadVoidSkuZipData(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/voids/download-sku-osr`, body);
  }

  getHistoryList(): Observable<any> {
    return this.http.get(`${this.baseUrl}/history`);
  }

  deleteHistoryById(historyId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/history/${historyId}`);
  }

  getHistoryDetails(historyId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/history/detail/${historyId}`);
  }

  getDuplicateFilterGroups(historyId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/history/duplicate-filter/${historyId}`);
  }

  getPreview(sessionId: string = '') {
    return this.http.get(`${this.baseUrl}/session/${sessionId}`);
  }

  createSession(sessionName: string = '') {
    return this.http.post(`${this.baseUrl}/session`, { sessionName });
  }

  updateSession(sessionName: string = '', sessionId: string) {
    return this.http.put(`${this.baseUrl}/session`, { sessionId, sessionName });
  }
  //save setup preference data
  PostUserPreference(endpoint: string, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${endpoint}`, body);
  }

  downloadUserGuide(): Observable<any> {
    return this.http.get(`${this.baseUrl}/users/download-user-guide`, {
      responseType: 'blob',
    });
  }

  getAzureProfile() {
    const headers = new HttpHeaders({
      accessToken: 'true',
    });
    return this.http.get(`https://graph.microsoft.com/v1.0/me/`, { headers });
  }

  getUserAvatarImage() {
    const headers = new HttpHeaders({
      accessToken: 'true',
    });
    return this.http.get(`https://graph.microsoft.com/v1.0/me/photo/$value`, {
      headers,
      responseType: 'arraybuffer',
    });
  }

  getDefaultVoidsData() {
    return this.http.get(`${this.baseUrl}/voids/get-default-voids-filter`);
  }

  applyVoidFilter(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/voids/apply-filter`, body);
  }

  getSkuSummary(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/voids/selected-sku-summary`, body);
  }

  getVoidDetail(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/voids/sku-page`, body);
  }

  getScreenAccess(): Observable<any> {
    return this.http.get(`${this.baseUrl}/users/get-user-screen-access`);
  }

  getBottlerDefaultScreen(): Observable<any> {
    return this.http.get(`${this.baseUrl}/users/get-bottler-default-screen`);
  }
}
